import React, { useEffect, useMemo, useState } from "react";
import { useRoute } from "@react-navigation/native";
import _ from "lodash";
import ThemedSafeAreaComponentTop from "components/atoms/ThemedSafeAreaTop";
import ThemedLayout from "components/atoms/ThemedLayout";
import ThemedBlock from "components/atoms/ThemedBlock";
import EmployeeCard, { EmployeeCardShowAll } from "components/molecules/EmployeeCard";
import FetchBooking from 'utils/fetch/FetchBooking'
import ThemedCalendarStrip from 'components/atoms/ThemedCalendarStrip'
import FetchCachedEmployeeDetails from 'utils/fetch/cached/FetchCachedEmployeeDetails'
import HelperLocaleSettings from 'utils/helper/HelperLocaleSettings'
import { localeSettingConstants } from 'utils/constants/LocaleSettings'
import HelperEmployeePrivacy from 'utils/HelperEmployeePrivacy'
import HelperEmployeeFavourite from 'utils/HelperEmployeeFavourite'
import useEmployee from 'utils/hooks/use-employee'
import HelperDate from 'utils/helper-date'
import { ThemedValues } from 'styles/themed'
import VStack from 'components/atoms/stack/VStack'

const HomeCoworkerViewScene = () => {
	
	const route = useRoute();
	
	const [ calendarDate, setCalendarDate ] = useState( HelperDate.date() );
	const [ calendarDateFetched, setCalendarDateFetched ] = useState( null );
	
	const [ employeeBookings, setEmployeeBookings ] = useState( {} );
	const [ isLoading, setIsLoading] = useState( true );
	
	const routeEmployee = _.get( route, ['params', 'employee'], {} );
	const employeeDetails = useEmployee( routeEmployee );
	
	const showCalendar = useMemo( () => {
		return HelperEmployeePrivacy.canShowCalendar( employeeDetails, HelperEmployeeFavourite.isFavourite( employeeDetails?.guid ) );
	}, [ employeeDetails ] )
	
	const fetchBookings = ( date ) => {
		setCalendarDateFetched( date );
		
		FetchBooking.fetchRange( date, {
			employeeGuid: routeEmployee,
			callbackStart: setIsLoading.bind( this, true ),
			callbackSuccess: ( { bookings, employee } ) => {
				setEmployeeBookings( {...employeeBookings, ...bookings } );
				
				setIsLoading( false )
			},
			callbackError: setIsLoading.bind( this, false )
		});
	}
	
	const onModeChange = ( mode ) => {
		HelperLocaleSettings.set( localeSettingConstants.CALENDAR_VIEW_MODE_BOOKINGS, mode );
	}
	
	useEffect( () => {
		if( showCalendar === true ) {
			setCalendarDate( HelperDate.date() );
		}
	}, [ showCalendar ] );
	
	useEffect( () => {
		if( !employeeDetails ) {
			FetchCachedEmployeeDetails.fetch( {
				employee_guids: [ routeEmployee ]
			} );
		}
	}, [ routeEmployee ])
	
	useEffect( () => {
		if( showCalendar === true ) {
			if( calendarDateFetched === null || !calendarDateFetched.isSame( calendarDate, 'day' ) ) {
				fetchBookings( calendarDate )
			}
		}
	}, [ showCalendar, calendarDate, calendarDateFetched ] );
	
	return (
		<ThemedSafeAreaComponentTop
			top={ false }>
			
			<ThemedLayout
				topNavigation={ true }
				showBackButton={ true }
				footerBar={ false }>
				
				<VStack
					space={ ThemedValues.stackSpace }>
					
					<ThemedBlock shadow>
						<EmployeeCard
							employee={ { guid: routeEmployee } }
							showDetails={ true }
							avatarSize={ 'm' }
							avatarShowIconEro={ true }
							avatarShowIconConfidant={ true }
							show={ EmployeeCardShowAll }
						/>
					</ThemedBlock>
					
					{ showCalendar && (
						<ThemedCalendarStrip
							key={ "calendar-strip" }
							employee={ { guid: routeEmployee } }
							onSelect={ setCalendarDate }
							bookings={ employeeBookings }
							showEmployee={ true }
							showLunchOptions={ false }
							showBuildingParkingSpace={ false }
							withActions={ false }
							onModeChange={ onModeChange }
							date={ calendarDate }
							isLoading={ isLoading }
						/>
					)}
				</VStack>
			</ThemedLayout>
		</ThemedSafeAreaComponentTop>
	)
}

export default HomeCoworkerViewScene;
